<template>
    <modal
        name="cargar-voucher"
        transition="pop-out"
        :width="720"
        height="auto"
        scrollable
        :adaptive="true"
        @before-open="onBeforeOpen"
        @closed="onClosed"
    >
        <div class="tw:bg-white tw:rounded-xl tw:shadow-lg tw:border tw:border-gray-200">
            <div class="tw:border-b tw:border-gray-200 tw:px-6 tw:py-4">
                <div class="tw:flex tw:items-start tw:justify-between">
                    <div class="tw:flex-1">
                        <h2 class="tw:text-xl tw:font-semibold tw:text-gray-900">Cargar descuentos</h2>
                        <p class="tw:text-sm tw:text-gray-500 tw:mt-1">
                            Crea cupones a partir de una temporada anterior o sube un archivo Excel.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-cy="cargar-voucher-close"
                        class="tw:text-gray-400 tw:hover:text-gray-600 tw:transition-colors tw:shrink-0 tw:ml-4"
                        @click.prevent="cerrarModal"
                    >
                        <span class="tw:text-4xl tw:leading-none" aria-label="Cerrar">×</span>
                    </button>
                </div>
            </div>

            <div class="tw:px-6 tw:py-4">
                <TabsPanel
                    :tabs="tabs"
                    initial-tab="temporada-anterior"
                    bare
                    content-class="tw:py-4"
                    @tab-change="onTabChange"
                >
                    <!-- Tab: temporada anterior (legacy flow, byte-identical action call) -->
                    <template #tab-temporada-anterior>
                        <ValidationObserver
                            tag="form"
                            ref="legacyObserver"
                            @submit.prevent="submitLegacy()"
                        >
                            <div class="tw:py-4 tw:px-1 tw:space-y-4">
                                <div v-if="legacyListas && legacyListas.length > 0">
                                    <div class="tw:mb-3">
                                        <label
                                            for="legacy-lista-antigua"
                                            class="tw:block tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-2"
                                        >
                                            Seleccione la temporada anterior
                                        </label>
                                        <ValidationProvider
                                            name="lista_antigua"
                                            id="legacy-lista-antigua"
                                            rules="required"
                                            v-slot="{ errors }"
                                        >
                                            <multiselect
                                                v-model="legacyVoucher.lista_antigua"
                                                placeholder="Seleccione la temporada anterior"
                                                :max-height="95"
                                                label="nombre"
                                                track-by="id"
                                                :options="legacyListas"
                                            />
                                            <span
                                                v-if="errors[0]"
                                                class="tw:text-sm tw:text-red-600 tw:mt-1 tw:block"
                                            >{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div v-if="legacyVoucher.lista_antigua" class="tw:grid tw:grid-cols-12 tw:gap-3">
                                    <FormInput
                                        class="tw:col-span-12"
                                        label="Nombre"
                                        name="legacy-nombre"
                                        rules="required"
                                        v-model="legacyVoucher.name"
                                    />
                                    <FormInput
                                        class="tw:col-span-6"
                                        label="Descuento (%)"
                                        name="legacy-descuento-porcentaje"
                                        type="number"
                                        v-model="legacyVoucher.discount_percent"
                                        :disabled="legacyVoucher.discount_fixed != 0"
                                    />
                                    <FormInput
                                        class="tw:col-span-6"
                                        label="Descuento (€)"
                                        name="legacy-descuento-fijo"
                                        type="number"
                                        v-model="legacyVoucher.discount_fixed"
                                        :disabled="legacyVoucher.discount_percent != 0"
                                    />
                                    <FormInput
                                        class="tw:col-span-12"
                                        label="Usos"
                                        name="legacy-usos"
                                        rules="required"
                                        type="number"
                                        v-model="legacyVoucher.units"
                                    />
                                    <div class="tw:col-span-12 tw:mt-2">
                                        <label
                                            class="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                data-cy="legacy-sin-vincular"
                                                v-model="legacyVoucher.sin_vincular"
                                            />
                                            Válido para todos los precios y regiones
                                        </label>
                                    </div>
                                    <div
                                        v-if="!legacyVoucher.sin_vincular"
                                        class="tw:col-span-12"
                                    >
                                        <label
                                            for="legacy-precios"
                                            class="tw:block tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-2"
                                        >
                                            Precios
                                        </label>
                                        <multiselect
                                            v-model="legacyVoucher.prices"
                                            placeholder="Precios"
                                            label="descripcion"
                                            track-by="id"
                                            :options="legacyPrecios"
                                            :multiple="true"
                                        />
                                    </div>
                                </div>

                                <div class="tw:flex tw:justify-end tw:pt-2">
                                    <b-button
                                        type="submit"
                                        data-cy="legacy-submit"
                                        :disabled="legacyLoading"
                                    >
                                        Guardar
                                    </b-button>
                                </div>
                            </div>
                        </ValidationObserver>
                    </template>

                    <!-- Tab: desde Excel (new flow) -->
                    <template #tab-desde-excel>
                        <div class="tw:py-4">
                            <ExcelImportContent
                                ref="excelContent"
                                @cerrar="onExcelCerrar"
                            />
                        </div>
                    </template>
                </TabsPanel>
            </div>
        </div>
    </modal>
</template>

<script>
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue'
    import ExcelImportContent from './ExcelImportContent.vue'
    import { mapGetters } from 'vuex'

    /**
     * ModalImportDiscountsTabs — unified "Cargar descuentos" modal.
     *
     * Renders a TabsPanel with two flows:
     *  - "Temporada anterior" — the legacy "import from a previous season" form
     *    that dispatches the byte-identical BACK_ABONOS_CUPONES_CARGAR action.
     *  - "Desde Excel" — the new Excel upload flow driven by the
     *    useCouponExcelImport composable.
     *
     * The legacy ModalImportDiscounts.vue and standalone
     * ModalImportDiscountsExcel.vue are kept in the repo for tests but are
     * not mounted in TabVouchers.vue anymore.
     */
    export default {
        name: 'ModalImportDiscountsTabs',
        components: { TabsPanel, ExcelImportContent },
        data() {
            return {
                tabs: [
                    { key: 'temporada-anterior', label: 'Temporada anterior' },
                    { key: 'desde-excel', label: 'Desde Excel' }
                ],
                legacyVoucher: {
                    discount_fixed: 0,
                    discount_percent: 0,
                    sin_vincular: false,
                    prices: []
                },
                legacyPrecios: [],
                legacyLoading: false,
                activeTab: 'temporada-anterior'
            }
        },
        computed: {
            ...mapGetters(['getListaId']),
            ...mapGetters(['getListas']),
            legacyListas() {
                return (this.getListas || []).filter(lista => lista.activa == 0)
            }
        },
        created() {
            this.refreshLegacyPrecios()
        },
        methods: {
            refreshLegacyPrecios() {
                const params = { lista_id: this.getListaId }
                this.$store
                    .dispatch(this.config.getVuexAlias('back.lista.vouchers.precios.disponibles'), params)
                    .then(res => {
                        this.legacyPrecios = res.data.data || []
                    })
            },
            async submitLegacy() {
                let isValid = await this.$refs.legacyObserver.validate()
                if (this.legacyVoucher.sin_vincular) {
                    this.legacyVoucher.prices = []
                }
                if (!isValid) return
                this.legacyLoading = true
                const prices = this.legacyVoucher.prices?.map(price => price.id)
                const params = {
                    lista_id: this.getListaId,
                    discount_fixed: this.legacyVoucher.discount_fixed,
                    discount_percent: this.legacyVoucher.discount_percent,
                    units: this.legacyVoucher.units,
                    sin_vincular: this.legacyVoucher.sin_vincular,
                    lista_antigua_id: this.legacyVoucher.lista_antigua.id,
                    prices: prices,
                    name: this.legacyVoucher.name
                }
                this.$store
                    .dispatch(this.config.getVuexAlias('back.abonos.cupones.cargar'), params)
                    .then(res => {
                        this.$toast.success(res.data.message)
                        this.cerrarModal()
                        this.$emit('creado')
                    })
                    .catch(error => {
                        this.$toast.error(error.response.data.message)
                    })
                    .finally(() => {
                        this.legacyLoading = false
                    })
            },
            onTabChange(key) {
                this.activeTab = key
                if (key === 'desde-excel' && this.$refs.excelContent) {
                    this.$refs.excelContent.reset()
                }
            },
            onExcelCerrar({ imported } = {}) {
                // The Excel content emits a single `cerrar` event with
                // `{ imported: boolean }`. We close the modal in both cases
                // and only re-emit `creado` to the parent when the import
                // actually succeeded — the parent uses that as the signal
                // to refresh the voucher list. Closing without importing
                // (errors, cancel, error rows) does NOT refresh the list.
                // The success toast is fired by the composable when the
                // phase transitions to 'imported', so the user gets
                // feedback even though the modal closes immediately.
                this.cerrarModal()
                if (imported) {
                    this.$emit('creado')
                }
            },
            cerrarModal() {
                this.$modal.hide('cargar-voucher')
            },
            onBeforeOpen() {
                this.activeTab = 'temporada-anterior'
                this.refreshLegacyPrecios()
            },
            onClosed() {
                if (this.$refs.excelContent) this.$refs.excelContent.reset()
            }
        }
    }
</script>
